import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Phone, Send, Check, Copy, Share2, Loader2, Trophy, Wallet,
  Film, Plus, Instagram, Youtube, LogOut, Sparkles
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const axi = axios.create({ baseURL: API_URL, withCredentials: true });

const TIER_META = {
  bronze: { label: "Bronze", color: "from-amber-600 to-amber-800", emoji: "🥉" },
  silver: { label: "Silver", color: "from-slate-400 to-slate-600", emoji: "🥈" },
  gold: { label: "Gold", color: "from-yellow-400 to-yellow-600", emoji: "🥇" },
  platinum: { label: "Platinum", color: "from-indigo-400 to-pink-500", emoji: "💎" },
};
const fmtINR = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

// ═══════════════════════ LOGIN ═══════════════════════
const LoginCard = ({ onSuccess }) => {
  const [phase, setPhase] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [instagram, setInstagram] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const sendOtp = async () => {
    if (!phone || phone.length < 10) return setErr("Enter a valid phone");
    setLoading(true); setErr("");
    try {
      await axi.post("/api/creators/auth/send-otp", { phone });
      setPhase("otp");
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to send OTP"); }
    setLoading(false);
  };

  const verify = async () => {
    if (!code || code.length < 4) return setErr("Enter the OTP");
    setLoading(true); setErr("");
    try {
      const r = await axi.post("/api/creators/auth/verify-otp", {
        phone, code, name, instagram,
      });
      onSuccess(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "OTP verification failed"); }
    setLoading(false);
  };

  return (
    <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-xl border border-gray-100" data-testid="creator-login-card">
      <div className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white rounded-full text-[10px] font-bold uppercase tracking-widest mb-2">
        <Sparkles size={12} /> Creator login
      </div>
      <h2 className="text-xl font-black text-[#1E1B4B]">Sign in to your dashboard</h2>
      <p className="text-xs text-gray-500 mt-1 mb-5">Track referrals, submit posts, claim rewards.</p>

      {phase === "phone" ? (
        <>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Phone</label>
          <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5 mb-3">
            <Phone size={14} className="text-gray-400" />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              placeholder="10-digit mobile"
              className="flex-1 bg-transparent text-sm outline-none"
              data-testid="creator-phone-input"
            />
          </div>
          <label className="block text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Name (optional)</label>
          <input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full bg-gray-50 rounded-xl px-3 py-2.5 text-sm outline-none mb-3"
            data-testid="creator-name-input"
          />
          <label className="block text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Instagram (optional)</label>
          <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2.5 mb-4">
            <Instagram size={14} className="text-gray-400" />
            <input
              value={instagram} onChange={(e) => setInstagram(e.target.value.replace("@", ""))}
              placeholder="handle"
              className="flex-1 bg-transparent text-sm outline-none"
              data-testid="creator-ig-input"
            />
          </div>
          {err && <p className="text-xs text-red-500 mb-2" data-testid="creator-login-err">{err}</p>}
          <button
            onClick={sendOtp}
            disabled={loading}
            className="w-full bg-[#E83E8C] text-white font-bold py-2.5 rounded-xl text-sm hover:bg-[#c22d78] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="creator-send-otp-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send OTP
          </button>
        </>
      ) : (
        <>
          <p className="text-xs text-gray-600 mb-3">OTP sent to <b>{phone}</b>. Enter the 6-digit code.</p>
          <input
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="------"
            className="w-full bg-gray-50 rounded-xl px-3 py-3 text-center text-xl font-mono font-bold outline-none tracking-[0.5em] mb-3"
            data-testid="creator-otp-input"
          />
          {err && <p className="text-xs text-red-500 mb-2" data-testid="creator-otp-err">{err}</p>}
          <button
            onClick={verify}
            disabled={loading}
            className="w-full bg-[#E83E8C] text-white font-bold py-2.5 rounded-xl text-sm hover:bg-[#c22d78] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="creator-verify-otp-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Verify & Enter
          </button>
          <button onClick={() => setPhase("phone")} className="w-full text-xs text-gray-500 mt-2 hover:text-[#E83E8C]">← Change number</button>
        </>
      )}
    </div>
  );
};

// ═══════════════════════ DASHBOARD ═══════════════════════
const Dashboard = ({ me, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState([]);
  const [showSubmit, setShowSubmit] = useState(false);
  const [copied, setCopied] = useState(false);
  const refLink = `${window.location.origin}/r/${me.referral_code}`;

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axi.get("/api/creators/leaderboard/me"),
        axi.get("/api/creators/content/mine"),
      ]);
      setStats(a.data);
      setContent(b.data.items || []);
    } catch { /* not fatal */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyLink = () => {
    navigator.clipboard.writeText(refLink);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({
        title: "Plan your house party with DoozyGo",
        text: "Use my code to get started →",
        url: refLink,
      }).catch(() => {});
    } else { copyLink(); }
  };

  const tier = TIER_META[stats?.me?.tier] || null;
  const monthMe = stats?.scopes?.month?.stats;

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="creator-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1E1B4B] to-[#7C3AED] rounded-3xl p-5 text-white">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold">
            {(me.name || "C").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1">
            <p className="text-xs opacity-70">Welcome back</p>
            <h2 className="font-bold text-lg" data-testid="creator-greeting">{me.name}</h2>
          </div>
          <button onClick={onLogout} className="p-2 rounded-lg bg-white/10 hover:bg-white/20" data-testid="creator-logout-btn">
            <LogOut size={14} />
          </button>
        </div>
        {tier && (
          <div className={`mt-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r ${tier.color} text-[11px] font-bold`}>
            {tier.emoji} {tier.label} tier
          </div>
        )}
      </div>

      {/* Referral link card */}
      <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm" data-testid="referral-card">
        <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Your referral link</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-gray-50 rounded-lg px-3 py-2 text-xs font-mono text-[#1E1B4B] truncate" data-testid="referral-link">{refLink}</code>
          <button onClick={copyLink} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200" data-testid="copy-ref-btn">
            {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
          </button>
          <button onClick={shareLink} className="p-2 rounded-lg bg-[#E83E8C] text-white hover:bg-[#c22d78]" data-testid="share-ref-btn">
            <Share2 size={14} />
          </button>
        </div>
        <p className="text-[10px] text-gray-500 mt-2">Each booking earns you ₹500 party credits + ranks you up the leaderboard.</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="stats-grid">
        <Stat icon={Trophy} label="Rank (month)" value={stats?.scopes?.month?.rank ? `#${stats.scopes.month.rank}` : "–"} color="indigo" />
        <Stat icon={Sparkles} label="Bookings (month)" value={monthMe?.bookings ?? 0} color="pink" />
        <Stat icon={Film} label="Posts verified" value={monthMe?.verified_uploads ?? 0} color="amber" />
        <Stat icon={Wallet} label="Credits" value={fmtINR(stats?.me?.credits_balance || 0)} color="emerald" />
      </div>

      {/* Rank across scopes */}
      <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm" data-testid="rank-across-scopes">
        <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-3">Your ranks</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {["month", "rolling_30", "season", "all_time"].map((s) => {
            const row = stats?.scopes?.[s];
            return (
              <div key={s} className="bg-gray-50 rounded-xl p-2 text-center" data-testid={`scope-rank-${s}`}>
                <p className="text-[10px] uppercase text-gray-500">{s.replace("_", " ")}</p>
                <p className="text-lg font-black text-[#1E1B4B]">{row?.rank ? `#${row.rank}` : "–"}</p>
                <p className="text-[9px] text-gray-500">of {row?.total_creators || 0}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Content submissions */}
      <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm" data-testid="content-card">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] uppercase tracking-wider font-bold text-gray-500">Your viral posts</p>
          <button
            onClick={() => setShowSubmit(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#E83E8C] text-white text-[10px] font-bold hover:bg-[#c22d78]"
            data-testid="submit-content-btn"
          >
            <Plus size={10} /> Submit post
          </button>
        </div>
        {content.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-4">No posts yet — share a reel to boost your score 📱</p>
        ) : (
          <div className="space-y-1.5">
            {content.map((c) => (
              <div key={c.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-2 py-1.5 text-xs" data-testid={`content-${c.id}`}>
                {c.platform === "instagram" ? <Instagram size={12} className="text-pink-500" /> : <Youtube size={12} className="text-red-500" />}
                <a href={c.url} target="_blank" rel="noreferrer" className="flex-1 truncate text-[#1E1B4B] hover:underline">{c.url}</a>
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${c.status === "verified" ? "bg-emerald-100 text-emerald-700" : c.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                  {c.status}
                </span>
                {c.verified_views > 0 && <span className="text-[10px] text-gray-500">{c.verified_views.toLocaleString()} views</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {showSubmit && <SubmitContentModal onClose={() => setShowSubmit(false)} onDone={() => { setShowSubmit(false); load(); }} />}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, color }) => {
  const colors = {
    indigo: "bg-indigo-50 text-indigo-700",
    pink: "bg-pink-50 text-pink-700",
    amber: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
  }[color];
  return (
    <div className={`rounded-2xl p-3 ${colors}`} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <Icon size={16} />
      <p className="text-[10px] uppercase tracking-wider font-bold opacity-70 mt-1">{label}</p>
      <p className="text-lg font-extrabold">{value}</p>
    </div>
  );
};

const SubmitContentModal = ({ onClose, onDone }) => {
  const [url, setUrl] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [views, setViews] = useState("");
  const [caption, setCaption] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!url.startsWith("http")) return setErr("Enter the full URL (https://...)");
    setLoading(true); setErr("");
    try {
      await axi.post("/api/creators/content/submit", {
        url, platform, estimated_views: parseInt(views || "0"), caption,
      });
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to submit"); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="submit-content-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl p-5">
        <h3 className="font-bold text-lg text-[#1E1B4B] mb-1">Submit a viral post</h3>
        <p className="text-xs text-gray-500 mb-4">We'll verify views within 48h. Verified posts boost your score.</p>

        <label className="block text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Platform</label>
        <div className="flex gap-1 mb-3">
          {["instagram", "youtube", "tiktok", "facebook"].map((p) => (
            <button key={p} onClick={() => setPlatform(p)} className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] font-bold capitalize ${platform === p ? "bg-[#E83E8C] text-white" : "bg-gray-100 text-gray-600"}`} data-testid={`platform-${p}`}>
              {p}
            </button>
          ))}
        </div>

        <label className="block text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Post URL</label>
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://instagram.com/reel/..." className="w-full bg-gray-50 rounded-xl px-3 py-2 text-sm outline-none mb-3" data-testid="content-url-input" />

        <label className="block text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Est. views (optional)</label>
        <input type="number" value={views} onChange={(e) => setViews(e.target.value)} placeholder="e.g. 12400" className="w-full bg-gray-50 rounded-xl px-3 py-2 text-sm outline-none mb-3" data-testid="content-views-input" />

        <label className="block text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Caption</label>
        <textarea value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="A short caption…" className="w-full bg-gray-50 rounded-xl px-3 py-2 text-sm outline-none mb-3 h-16" data-testid="content-caption-input" />

        {err && <p className="text-xs text-red-500 mb-2">{err}</p>}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-bold">Cancel</button>
          <button onClick={submit} disabled={loading} className="flex-1 py-2.5 rounded-xl bg-[#E83E8C] text-white text-sm font-bold hover:bg-[#c22d78] disabled:opacity-50 flex items-center justify-center gap-1" data-testid="submit-content-confirm">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════ PAGE ═══════════════════════
export default function CreatorDashboard() {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    axi.get("/api/creators/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => setMe(null))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    try { await axi.post("/api/creators/auth/logout"); } catch { /* ignore */ }
    setMe(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF0F5] via-white to-[#FFF7ED] py-6 px-4" data-testid="creator-dashboard-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-[#E83E8C]" data-testid="dashboard-back">
            <ArrowLeft size={14} /> Home
          </Link>
          <Link to="/creators/leaderboard" className="text-xs text-[#E83E8C] font-bold hover:underline" data-testid="dashboard-leaderboard">
            View Leaderboard →
          </Link>
        </div>
        {checking ? (
          <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin text-[#E83E8C]" /></div>
        ) : me ? (
          <Dashboard me={me} onLogout={logout} />
        ) : (
          <div className="flex justify-center py-10">
            <LoginCard onSuccess={setMe} />
          </div>
        )}
      </div>
    </div>
  );
}
